import { motion, AnimatePresence } from 'framer-motion'
import { useState } from 'react'

const ProjectsSection = () => {
  const projects = [
    {
      id: 1,
      title: 'Metalytics – Clinical Trial Automation Platform',
      shortform: 'Metalytics',
      description: 'Developed a metadata-driven system that auto-generates statistical programs (R, Python, SAS) for clinical trial reporting',
      technologies: ['Laravel', 'React', 'Docker', 'MySQL', 'Bitbucket'],
      achievements: [
        'Reduced programming redundancy and manual coding efforts by over 60% for statistical programmer',
        'Designed microservices architecture and implemented CI/CD with Docker reduced environment setup time from 1 day to under 1 hour',
        'Used by major European pharmaceutical companies to streamline trial reporting workflows'
      ],
      category: 'Healthcare',
      image: '/project1.jpg'
    },
    {
      id: 2,
      title: 'Risk-Based Monitoring System',
      shortform: 'RBM System',
      description: 'Built a monitoring platform for clinical trials  to improve risk assessment and compliance',
      technologies: ['NodeJS', 'D3js', 'LeafletJS', 'MySQL', 'Healthcare Analytics'],
      achievements: [
        'Integrated real-time data visualizations with D3 and LeafletJS for monitoring KPIs',
        'Led automation of testing and deployment to reduce QA effort and improve stability'
      ],
      category: 'Healthcare',
      image: '/project2.jpg'
    },
    {
      id: 3,
      title: 'Smart Distribution System',
      shortform: 'Smart Distribution',
      description: 'Served millions of family cardholders; integrated with Bangladesh NID database for user verification',
      technologies: ['Laravel', 'MySQL-Redis', 'Docker', 'Queues & Events'],
      achievements: [
        'Reduced card generation time using Laravel Events and batch processing via Queues',
        'Played a key role in backend optimization and Docker-based deployment strategy'
      ],
      category: 'GovTech',
      image: '/project3.jpg'
    },
    {
      id: 4,
      title: 'PickNRide – IoT Ride-Sharing Platform',
      shortform: 'PickNRide',
      description: 'Built secure token-based authentication APIs for IoT-enabled devices and mobile apps',
      technologies: ['Laravel + Passport', 'AWS', 'MySQL', 'Stripe', 'Swagger API'],
      achievements: [
        'Integrated payment gateway using Stripe and deployed services on AWS',
        'Acknowledged by Qatar Police and featured during FIFA World Cup 2022 & Qatar Sustainability Week 2021'
      ],
      category: 'IoT',
      image: '/project4.jpg'
    },
    {
      id: 5,
      title: 'Animated Presentation Tool – Pharma Data Visualization',
      shortform: 'Animated Presentation Tool',
      description: 'Created dynamic, interactive dashboards for clinical researchers',
      technologies: ['Laravel', 'D3.js', 'Pharma R&D'],
      achievements: [
        'Enabled real-time presentation of complex statistical datasets for better storytelling and analysis'
      ],
      category: 'Healthcare',
      image: '/project5.jpg'
    }
  ]

  const [activeCategory, setActiveCategory] = useState('All')
  const categories = ['All', 'Healthcare', 'GovTech', 'IoT']

  const filteredProjects = activeCategory === 'All' 
    ? projects 
    : projects.filter(project => project.category === activeCategory)

  return (
    <section id="projects" className="py-20 relative">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section header */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Featured Projects
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-purple-400 mx-auto rounded-full" />
          </motion.div>

          {/* Category filter */}
          <motion.div
            className="flex flex-wrap justify-center gap-4 mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {categories.map((category) => (
              <motion.button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2 rounded-full transition-all duration-300 ${
                  activeCategory === category
                    ? 'bg-blue-500 text-white'
                    : 'bg-white/10 text-gray-300 hover:bg-white/20'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {category}
              </motion.button>
            ))}
          </motion.div>

          {/* Projects grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <AnimatePresence mode="popLayout">
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  className="glass-light rounded-2xl overflow-hidden group"
                  layout
                  initial={{ opacity: 0, scale: 0.9, y: 30 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: -30 }}
                  transition={{ 
                    duration: 0.5,
                    delay: index * 0.08,
                    ease: [0.25, 0.1, 0.25, 1]
                  }}
                  viewport={{ once: true }}
                  whileHover={{ 
                    y: -8,
                    scale: 1.02,
                    transition: { 
                      duration: 0.3,
                      ease: "easeInOut"
                    }
                  }}
                >
                  {/* Project image placeholder */}
                  <motion.div 
                    className="relative h-48 overflow-hidden"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center">
                      <div className="text-4xl font-bold text-white/30">
                        {project.shortform}
                      </div>
                    </div>
                    <motion.div 
                      className="absolute top-4 right-4 bg-blue-500 text-white px-3 py-1 rounded-full text-sm"
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.2 }}
                    >
                      {project.category}
                    </motion.div>
                  </motion.div>

                  {/* Project content */}
                  <div className="p-6">
                    <motion.h3 
                      className="text-xl font-bold text-white mb-3"
                      whileHover={{ color: 'rgb(96, 165, 250)' }}
                      transition={{ duration: 0.3 }}
                    >
                      {project.title}
                    </motion.h3>
                    <p className="text-gray-300 mb-4 text-sm leading-relaxed">
                      {project.description}
                    </p>

                    {/* Technologies */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.technologies.map((tech, techIndex) => (
                        <motion.span
                          key={techIndex}
                          className="bg-white/10 text-gray-400 px-2 py-1 rounded text-xs"
                          whileHover={{ 
                            scale: 1.1,
                            backgroundColor: 'rgba(255, 255, 255, 0.2)'
                          }}
                          transition={{ duration: 0.2 }}
                        >
                          {tech}
                        </motion.span>
                      ))}
                    </div>

                    {/* Achievements */}
                    <div className="space-y-2 mb-4">
                      <h4 className="text-sm font-semibold text-white">Key Achievements:</h4>
                      <ul className="text-xs text-gray-400 space-y-1">
                        {project.achievements.slice(0, 2).map((achievement, achIndex) => (
                          <motion.li 
                            key={achIndex} 
                            className="flex items-start gap-2"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 + achIndex * 0.1 }}
                          >
                            <motion.span 
                              className="text-blue-400"
                              whileHover={{ scale: 1.2, rotate: 45 }}
                              transition={{ duration: 0.3 }}
                            >•</motion.span>
                            <span>{achievement}</span>
                          </motion.li>
                        ))}
                      </ul>
                    </div>

                    {/* View details button */}
                    <motion.button
                      className="w-full bg-blue-500/20 text-blue-400 py-2 rounded-lg transition-all duration-300 text-sm font-medium"
                      whileHover={{ 
                        scale: 1.05,
                        backgroundColor: 'rgba(59, 130, 246, 0.4)'
                      }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                    >
                      View Details
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Call to action */}
          <motion.div
            className="text-center mt-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <p className="text-gray-400 mb-4">
              Interested in seeing more of my work? Let's connect!
            </p>
            <motion.a
              href="#contact"
              className="glass-light px-8 py-3 rounded-full inline-flex items-center gap-2 hover:bg-white/20 transition-all duration-300"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              Get In Touch
            </motion.a>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default ProjectsSection